import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useTutorial } from "../../contexts/TutorialContext.jsx";
import Style from '../../styles/components/tutorial/TutorialOverlay.module.css';

const PADDING = 16;
const RADIUS = 14;
const TOOLTIP_HEIGHT = 200;
const ANIM_MS = 320; // duração da animação do spotlight

export default function TutorialOverlay() {
  const { active, steps, currentStep, nextStep, closeTutorial } = useTutorial();

  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState("bottom");
  const [tooltipVisible, setTooltipVisible] = useState(false); // controla fade do tooltip
  const tooltipRef = useRef(null);
  const step = steps[currentStep];

  useLayoutEffect(() => {
    if (!active || !step) return;

    // esconde tooltip imediatamente ao trocar de step
    setTooltipVisible(false);

    const findAndSet = () => {
      const el = document.querySelector(`[data-tutorial-id="${step.targetId}"]`);
      if (!el) {
        setRect(null);
        setTimeout(() => setTooltipVisible(true), 80);
        return;
      }

      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });

      const afterScroll = setTimeout(() => {
        const r = el.getBoundingClientRect();
        setRect({ x: r.x, y: r.y, w: r.width, h: r.height });

        const spaceBelow = window.innerHeight - (r.y + r.height);
        setTooltipPos(spaceBelow > TOOLTIP_HEIGHT + 32 ? "bottom" : "top");

        // tooltip aparece após spotlight chegar na posição
        setTimeout(() => setTooltipVisible(true), ANIM_MS);
      }, 400);

      return () => clearTimeout(afterScroll);
    };

    const t = setTimeout(findAndSet, 60);
    return () => clearTimeout(t);
  }, [active, step, currentStep]);

  useEffect(() => {
    if (!active || !step) return;
    const recalc = () => {
      const el = document.querySelector(`[data-tutorial-id="${step.targetId}"]`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ x: r.x, y: r.y, w: r.width, h: r.height });
    };
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [active, step]);

  if (!active || !step) return null;

  const svgW = window.innerWidth;
  const svgH = window.innerHeight;
  const hasFocus = !!rect;

  const sx = hasFocus ? rect.x - PADDING : svgW / 2 - 60;
  const sy = hasFocus ? rect.y - PADDING : svgH / 2 - 40;
  const sw = hasFocus ? rect.w + PADDING * 2 : 120;
  const sh = hasFocus ? rect.h + PADDING * 2 : 80;

  const tooltipW = Math.min(260, svgW - 32);
  const tooltipCenterX = sx + sw / 2;
  const tooltipLeft = Math.max(16, Math.min(tooltipCenterX - tooltipW / 2, svgW - tooltipW - 16));

  let tooltipTop;
  if (!hasFocus) {
    tooltipTop = svgH / 2 - TOOLTIP_HEIGHT / 2;
  } else if (tooltipPos === "bottom") {
    tooltipTop = sy + sh + 24;
  } else {
    tooltipTop = sy - TOOLTIP_HEIGHT - 24;
  }
  tooltipTop = Math.max(8, Math.min(tooltipTop, svgH - TOOLTIP_HEIGHT - 8));

  const isLast = currentStep === steps.length - 1;

  return (
    <div className={Style.root}>
      <svg className={Style.svg} width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
        <defs>
          <mask id="tut-mask">
            <rect width={svgW} height={svgH} fill="white" />
            <rect
              className={Style.spotlightHole}
              x={sx} y={sy} width={sw} height={sh} rx={RADIUS}
              fill="black"
            />
          </mask>
        </defs>
        <rect width={svgW} height={svgH} fill="rgba(0,0,0,0.72)" mask="url(#tut-mask)" />
        <rect
          className={Style.spotlightBorder}
          x={sx - 1.5} y={sy - 1.5}
          width={sw + 3} height={sh + 3}
          rx={RADIUS + 1}
          fill="none"
          stroke="rgba(255,255,255,0.25)"
          strokeWidth="1.5"
        />
      </svg>

      <div
        ref={tooltipRef}
        className={`${Style.tooltip} ${tooltipVisible ? Style.tooltipVisible : ""}`}
        style={{ left: tooltipLeft, top: tooltipTop, width: tooltipW }}
      >
        {hasFocus && (
          <div
            className={`${Style.arrow} ${tooltipPos === "top" ? Style.arrowDown : Style.arrowUp}`}
            style={{ left: Math.max(12, tooltipCenterX - tooltipLeft - 8) }}
          />
        )}
        <div className={Style.tooltipInner}>
          <div className={Style.stepCount}>{currentStep + 1} / {steps.length}</div>
          <h6 className={Style.tooltipTitle}>{step.title}</h6>
          <p className={Style.tooltipDesc}>{step.description}</p>
          <div className={Style.tooltipActions}>
            <button className={Style.btnSkip} onClick={closeTutorial}>Pular</button>
            <button className={Style.btnNext} onClick={nextStep}>
              {isLast ? "Concluir ✓" : "Próximo →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}